import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { loadJoin, saveJoin } from "@/lib/pwa";
import { BRAND } from "@/config/brand";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "@/lib/contact";
import { Logo, Blason } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Ambulance, Hospital, CalendarClock, History, PlayCircle, MonitorSmartphone, BarChart3, Building2, HeartHandshake, ArrowLeftRight } from "lucide-react";
import { PatientLoginForm } from "@/pages/patient/PatientLoginForm";
import { setSpace } from "@/lib/space";

// Choix d'espace : deux gros boutons, même page visuelle, authentifications techniquement séparées.
export function SpaceTabs({ space, big = false }) {
  const opts = [
    { key: "entreprise", to: "/login", icon: Building2, title: "Espace entreprise", sub: "Chauffeur • Régulation • Administration", on: "bg-brand-800 border-brand-800 text-white", off: "bg-white border-brand-200 text-brand-900 hover:border-brand-500", ic: "bg-white/15 text-white", icOff: "bg-brand-50 text-brand-800" },
    { key: "patient", to: "/patient/connexion", icon: HeartHandshake, title: "Espace patient", sub: "Transmettre et suivre mes rendez-vous", on: "bg-green-600 border-green-600 text-white", off: "bg-white border-green-200 text-green-900 hover:border-green-500", ic: "bg-white/15 text-white", icOff: "bg-green-50 text-green-700" },
  ];
  return (
    <div data-testid="space-tabs" role="tablist" className={`grid gap-3 ${big ? "grid-cols-1" : "grid-cols-2"}`}>
      {opts.map((o) => { const act = space === o.key; return (
        <Link key={o.key} to={o.to} role="tab" aria-selected={act} data-testid={`space-tab-${o.key}`} onClick={() => setSpace(o.key)}
          className={`rounded-2xl border-2 text-left transition-colors active:scale-[0.98] ${big ? "p-5 flex items-center gap-4" : "p-3 flex flex-col items-start gap-1.5"} ${act ? o.on : o.off}`}>
          <span className={`${big ? "w-14 h-14" : "w-9 h-9"} rounded-xl flex items-center justify-center shrink-0 ${act ? o.ic : o.icOff}`}><o.icon className={big ? "w-7 h-7" : "w-5 h-5"} /></span>
          <span><span className={`block font-heading font-extrabold ${big ? "text-lg" : "text-sm"}`}>{o.title}</span><span className={`block ${big ? "text-sm" : "text-[11px]"} ${act ? "opacity-85" : "text-slate-500"}`}>{o.sub}</span></span>
        </Link>); })}
    </div>
  );
}

const PILLARS = [
  { icon: Ambulance, label: "Chauffeurs", text: "Retour terrain en quelques gestes" },
  { icon: Hospital, label: "Établissements", text: "Documents regroupés par lieu" },
  { icon: CalendarClock, label: "Suivi automatique", text: "Dates de récupération et rappels" },
  { icon: History, label: "Traçabilité", text: "Historique complet" },
];

const DEMOS = [
  { key: "chauffeur", icon: Ambulance, label: "Démo Chauffeur", text: "Smartphone · récupération en 3 gestes" },
  { key: "regulation", icon: MonitorSmartphone, label: "Démo Régulation", text: "Tableau de bord et affectations" },
  { key: "dirigeant", icon: BarChart3, label: "Démo Dirigeant", text: "Points, récompenses, abonnement" },
];

const homeFor = (r) => (r === "chauffeur" ? "/driver" : r === "superadmin" ? "/super" : "/app");

// Tracé ECG discret en filigrane (aucune croix rouge)
function EcgLine() {
  const beat = "h60 l8 0 l8 -16 l8 32 l8 -46 l8 58 l8 -28 h68 l8 -8 l8 14 l8 -6 h60";
  const d = `M0 60 ${beat} ${beat} ${beat} ${beat}`;
  return (
    <svg className="absolute inset-x-0 bottom-28 w-full h-28 opacity-25" viewBox="0 0 1200 120" preserveAspectRatio="none" aria-hidden>
      <path d={d} fill="none" stroke="#A9DDFF" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"
        style={{ strokeDasharray: 1400, strokeDashoffset: 1400, animation: "ecg-draw 7s linear infinite" }} />
      <style>{`@keyframes ecg-draw { 0% { stroke-dashoffset: 1400; opacity: .3 } 70% { opacity: 1 } 100% { stroke-dashoffset: -1400; opacity: .3 } }`}</style>
    </svg>
  );
}

export default function LoginPage({ reserved = false, space = "entreprise" }) {
  const { login, demoLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoLoading, setDemoLoading] = useState("");
  const [joinInfo, setJoinInfo] = useState(null);
  const [params] = useSearchParams();
  const [demoExpired] = useState(() => {
    const exp = localStorage.getItem("sanitdocs_demo_expires");
    if (!exp || exp > new Date().toISOString()) return false;
    ["sanitdocs_demo_expires", "sanitdocs_demo_sandbox", "sanitdocs_demo_tour"].forEach((k) => localStorage.removeItem(k));
    return true;
  });
  useEffect(() => { if (demoExpired) { window.dispatchEvent(new Event("sanitdocs-demo")); } }, [demoExpired]);
  // Lien direct depuis le site vitrine : /login?demo=1 (choix du profil) ou /login?demo=chauffeur|regulation|dirigeant
  useEffect(() => {
    const p = params.get("demo");
    if (!p) return;
    if (["chauffeur", "regulation", "dirigeant"].includes(p)) startDemo(p); else setDemoOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const j = loadJoin();
    if (!j?.email || !j?.code) return;
    setEmail((e) => e || j.email);
    setJoinInfo(j);
    api.get("/access-requests/status", { params: { code: j.code, email: j.email } })
      .then((r) => { const upd = { ...j, status: r.data.status }; saveJoin(upd); setJoinInfo(upd); })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    setSpace("entreprise");
    // Permet aux gestionnaires de mots de passe (Chrome/Google, Safari) de proposer l'enregistrement.
    try {
      if (window.PasswordCredential && e.target) {
        const cred = new window.PasswordCredential(e.target);
        await navigator.credentials.store(cred);
      }
    } catch (_) {}
    nav(homeFor(res.user.role), { replace: true });
  };

  const startDemo = async (key) => {
    setDemoLoading(key);
    const res = await demoLogin(key);
    setDemoLoading("");
    if (!res.ok) { setError(res.error); setDemoOpen(false); return; }
    setSpace("entreprise");
    nav(homeFor(res.user.role), { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      <div className="hidden lg:flex lg:w-[46%] bg-brand-800 brand-grid text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full bg-accent-500/25 blur-3xl" />
        <div className="absolute -left-16 bottom-0 w-80 h-80 rounded-full bg-brand-500/40 blur-3xl" />
        <div className="absolute right-10 bottom-10 opacity-[0.12]"><Blason size={380} /></div>
        <EcgLine />
        <div className="relative"><Logo size={44} onDark /></div>
        <div className="relative">
          <p className="text-accent-300 font-semibold tracking-widest uppercase text-xs mb-3">Transport sanitaire</p>
          <h1 className="text-4xl xl:text-5xl font-heading font-extrabold leading-[1.1]">{BRAND.tagline}</h1>
          <div className="grid grid-cols-2 gap-3 mt-8 max-w-lg">
            {PILLARS.map((p) => (
              <div key={p.label} className="flex items-start gap-3 rounded-xl bg-white/[0.07] border border-white/10 p-3.5">
                <div className="w-9 h-9 rounded-lg bg-accent-500/20 text-accent-200 flex items-center justify-center shrink-0"><p.icon className="w-5 h-5" /></div>
                <div><p className="font-heading font-bold text-sm">{p.label}</p><p className="text-brand-100 text-xs">{p.text}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-brand-200 text-sm">{BRAND.copyright}</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 animate-fade-in">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-6 flex flex-col items-center gap-2"><Blason size={space === null ? 170 : 120} /><p className="text-xs font-medium text-slate-500">{BRAND.tagline}</p></div>
          {!reserved && space === null && (
            <div data-testid="space-choice" className="animate-fade-in">
              <h2 className="text-2xl font-heading font-bold text-brand-900">Bienvenue sur SanitDocs</h2>
              <p className="text-slate-500 mt-1 mb-6 text-sm">Choisissez votre espace pour continuer.</p>
              <SpaceTabs space={null} big />
            </div>
          )}
          {!reserved && space !== null && <div className="mb-6"><SpaceTabs space={space} /></div>}
          {!reserved && space === "patient" && (
            <div data-testid="patient-space-panel">
              <h2 className="text-2xl font-heading font-bold text-green-900">Espace patient</h2>
              <p className="text-slate-500 mt-1 mb-4 text-sm">Transmettez et suivez vos rendez-vous.</p>
              <PatientLoginForm />
              <p className="mt-5 text-xs text-slate-400 text-center">Vous êtes un professionnel ? <Link data-testid="patient-to-enterprise-link" to="/login" onClick={() => setSpace("entreprise")} className="underline">Accéder à l'Espace entreprise</Link></p>
              <Link to="/choisir" data-testid="change-space-link" className="mt-2 flex items-center justify-center gap-1 text-xs font-semibold text-slate-500 hover:underline"><ArrowLeftRight className="w-3.5 h-3.5" />Changer d'espace</Link>
            </div>
          )}
          {(reserved || space === "entreprise") && <>
          <h2 className="text-2xl font-heading font-bold text-brand-900">{reserved ? "Accès réservé" : "Espace entreprise"}</h2>
          <p className="text-slate-500 mt-1 mb-6 text-sm">{reserved ? "Administration de la plateforme." : "Chauffeur • Régulation • Administration"}</p>
          {joinInfo && !reserved && (
            <div data-testid="join-pending-banner" className="mb-4 text-sm bg-accent-50 border border-accent-200 rounded-xl px-3 py-2.5 text-accent-800">
              <b>{joinInfo.organization_name}</b> — {joinInfo.status === "accepted" ? "votre accès a été validé : connectez-vous avec votre email et votre mot de passe." : joinInfo.status === "refused" ? "votre demande a été refusée, contactez votre entreprise." : "votre demande d'accès est en attente de validation par votre dirigeant. Vous pourrez vous connecter dès qu'elle sera acceptée."}
            </div>
          )}

          <form onSubmit={submit} method="post" action="/login" className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input id="email" name="email" type="email" inputMode="email" data-testid="login-email-input" value={email} autoComplete="username"
                onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.fr" className="mt-1 h-12 bg-white" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
              <Input id="password" name="password" type="password" data-testid="login-password-input" value={password} autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1 h-12 bg-white" required />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full h-12 text-base font-bold bg-accent-600 hover:bg-accent-700 shadow-md shadow-accent-600/25 transition-colors">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Se connecter
            </Button>
          </form>
          <p className="mt-4 text-xs text-slate-400 text-center">Pas encore abonné ? <a data-testid="login-contact-link" href={CONTACT_MAILTO} className="underline text-accent-700 font-semibold">Commander un abonnement</a> · <a href={`mailto:${CONTACT_EMAIL}`} data-testid="login-contact-email" className="underline">{CONTACT_EMAIL}</a></p>
          <p className="mt-3 text-xs text-slate-400 text-center">Vous êtes patient ? <Link data-testid="login-patient-link" to="/patient/connexion" onClick={() => setSpace("patient")} className="underline">Accéder à l'Espace patient</Link></p>
          {!reserved && <Link to="/choisir" data-testid="change-space-link" className="mt-2 flex items-center justify-center gap-1 text-xs font-semibold text-slate-500 hover:underline"><ArrowLeftRight className="w-3.5 h-3.5" />Changer d'espace</Link>}

          {!reserved && (
            <>
              <Link to="/rejoindre" data-testid="join-link" className="block text-center text-sm font-semibold text-accent-700 mt-4 hover:underline">
                Chauffeur ? Rejoindre une entreprise
              </Link>
              <div className="mt-6 pt-5 border-t border-slate-200">
                <Button type="button" variant="outline" data-testid="demo-button" onClick={() => setDemoOpen(true)}
                  className="w-full h-12 font-bold border-brand-200 text-brand-800 hover:bg-brand-50 hover:text-brand-900">
                  <PlayCircle className="w-5 h-5 mr-2 text-accent-600" />Découvrir SanitDocs en démo
                </Button>
                <p className="text-center text-xs text-slate-400 mt-2">Données fictives · aucune inscription</p>
                {demoExpired && <p className="text-center text-xs font-semibold text-amber-700 mt-2" data-testid="demo-expired-msg">Votre démonstration a expiré (24 h) : relancez une démo vierge.</p>}
              </div>
            </>
          )}
          </>}
        </div>
      </div>

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="max-w-md" data-testid="demo-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-900">Choisissez un profil de démonstration</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {DEMOS.map((d) => (
              <button key={d.key} data-testid={`demo-${d.key}`} disabled={!!demoLoading} onClick={() => startDemo(d.key)}
                className="w-full flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-accent-300 hover:bg-accent-50 transition-colors disabled:opacity-60">
                <div className="w-12 h-12 rounded-xl bg-brand-700 text-white flex items-center justify-center shrink-0">
                  {demoLoading === d.key ? <Loader2 className="w-6 h-6 animate-spin" /> : <d.icon className="w-6 h-6" />}
                </div>
                <div><p className="font-heading font-bold text-brand-900">{d.label}</p><p className="text-sm text-slate-500">{d.text}</p></div>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 text-center">Environnement de démonstration — données fictives uniquement.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
